/**
 * The credits bar on the sidebar card and on Créditos: what is left of this month's credits.
 * The baseline is the month's opening balance plus everything added since, so a grant mid-month
 * widens the bar rather than overfilling it; the fill is `(baseline - spent) / baseline`.
 */

import type { ReactNode } from 'react';
import { Coins } from 'lucide-react';

import { MeterBar, type MeterTone } from '../../../chrome/MeterBar';
import { getLocale } from '../../../i18n';
import { credits as formatCredits } from '../../../i18n/format';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../ui/tooltip';
import type { Credits } from '../api/schemas';
import { bm } from './messages';

export interface MonthCredits {
  opening: number;
  added: number;
  spent: number;
  baseline: number;
  available: number;
  /** `null` when the baseline is not positive: there is nothing to take a fraction of. */
  fraction: number | null;
}

export function monthCredits(credits: Credits): MonthCredits {
  const opening = credits.month_opening ?? 0;
  const added = credits.month_added ?? 0;
  const spent = credits.month_spent ?? 0;
  const baseline = opening + added;
  const available = baseline - spent;
  const fraction = baseline > 0 ? Math.min(Math.max(available / baseline, 0), 1) : null;
  return { opening, added, spent, baseline, available, fraction };
}

export function monthTone(fraction: number | null): MeterTone {
  if (fraction === null) return 'muted';
  if (fraction > 0.5) return 'primary';
  if (fraction >= 0.2) return 'warning';
  return 'destructive';
}

const ICON_TONE: Record<MeterTone, string> = {
  primary: 'text-primary-ink',
  warning: 'text-utilisation-mid',
  destructive: 'text-destructive',
  muted: 'text-muted-foreground',
};

export function CreditsStatusIcon(props: { credits: Credits }): ReactNode {
  const tone = monthTone(monthCredits(props.credits).fraction);
  return <Coins aria-hidden data-tone={tone} className={`size-4 shrink-0 ${ICON_TONE[tone]}`} />;
}

const TEXT_TONE: Record<MeterTone, string> = {
  primary: 'text-primary-ink',
  warning: 'text-utilisation-mid',
  destructive: 'text-destructive',
  muted: 'text-popover-foreground',
};

function Row(props: {
  label: string;
  value: string;
  className?: string;
  valueClassName?: string;
}): ReactNode {
  const { label, value, className = '', valueClassName = '' } = props;
  return (
    <>
      <dt className={className}>{label}</dt>
      <dd className={`pl-4 text-right tabular-nums ${className} ${valueClassName}`}>{value}</dd>
    </>
  );
}

export function MonthCreditsMeter(props: { credits: Credits }): ReactNode {
  const month = monthCredits(props.credits);
  const tone = monthTone(month.fraction);
  const locale = getLocale();
  const monthName = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }).format(
    new Date(),
  );
  const figures = {
    opening: formatCredits(month.opening, locale),
    added: formatCredits(month.added, locale),
    spent: formatCredits(month.spent, locale),
    available: formatCredits(month.available, locale),
    percent: new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(
      month.fraction ?? 0,
    ),
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div data-testid="month-credits-meter" className="py-1">
          <MeterBar
            value={month.fraction === null ? null : month.available}
            max={month.baseline}
            tone={tone}
            label={bm('month.label')}
          />
          {/* The tooltip is pointer-only; this carries the same figures to a screen reader. */}
          <span className="sr-only">{bm('month.summary', figures)}</span>
        </div>
      </TooltipTrigger>
      {/* The popover surface rather than the default inverted one, so the status tokens keep
          the contrast they were chosen for on the page; the primitive's arrow is drawn in the
          inverted colour, so it is hidden. */}
      <TooltipContent
        aria-hidden
        className="max-w-none min-w-56 border bg-popover px-3 py-2 text-popover-foreground shadow-2 [&>span]:hidden"
      >
        <p className="mb-1.5 text-muted-foreground capitalize">{monthName}</p>
        <dl className="grid grid-cols-label-value gap-y-1">
          <Row label={bm('month.opening')} value={figures.opening} />
          <Row label={bm('month.added')} value={figures.added} />
          <Row label={bm('month.spent')} value={figures.spent} />
          <Row
            label={bm('month.available')}
            value={`${figures.available} (${figures.percent})`}
            className="mt-0.5 border-t pt-1.5 font-semibold"
            valueClassName={TEXT_TONE[tone]}
          />
        </dl>
      </TooltipContent>
    </Tooltip>
  );
}
